import React, { useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import Spinner from '../../../util/spinner-shim'
import { injectIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'

import AppDispatcher from '../../../dispatchers/AppDispatcher'
import AppStore from '../../../stores/AppStore'

import ChartGeneric, { makeRows, makeColumns } from './ChartGeneric'
import ChartDataLoading from './ChartDataLoading'

const TableCell = function TableCell ({ value }) {
  const returnVal = (
    <td>
      {value}
    </td>
  )
  return returnVal
}

TableCell.propTypes = {
  value: PropTypes.node,
}

const TableRow = function TableRow ({ data }) {
  const cells = []
  for (let i = 0; i < data.length; i += 1) {
    if (i !== 1) {
      let cellValue
      const value = data[i]
      if (value != null && typeof value.getMonth === 'function') {
        cellValue = (
          <>
            {value.toLocaleDateString()}
            <br />
            {value.toLocaleTimeString()}
          </>
        )
      } else if (value != null) {
        cellValue = value
      } else {
        cellValue = 'N/A'
      }
      cells.push(
        <TableCell key={i} value={cellValue} />
      )
    }
  }
  return (
    <tr>
      {cells}
    </tr>
  )
}

TableRow.propTypes = {
  data: PropTypes.array,
}

const Table = function Table ({
  sensorData,
  device,
  dataType,
  dataName,
  conversion,
  multiplier,
  precision,
  formatMessage,
}) {
  const { tempConv } = AppStore.getUser()?.locale || { tempConv: 'c' }

  if (sensorData.length < 1) {
    return (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
    )
  }
  const rows = makeRows(sensorData, dataName, device, dataType, conversion, tempConv, multiplier, precision, true)
  const rowsArr = []
  for (let i = 0; i < rows.length; i += 1) {
    rowsArr.push(
      <TableRow key={i} data={rows[i]} />
    )
  }
  const columns = makeColumns(dataName, dataType, formatMessage)
  const columnArr = []
  for (let i = 0; i < columns.length; i += 1) {
    columnArr.push(columns[i].label)
  }
  return (
    <div className='telemetryTableWrapper'>
      <table className='table table-striped'>
        <thead>
          <TableRow data={columnArr} />
        </thead>
        <tbody>
          {rowsArr}
        </tbody>
      </table>
    </div>
  )
}

Table.propTypes = {
  sensorData: PropTypes.arrayOf(PropTypes.object),
  dataType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  dataName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  multiplier: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.array,
  ]),
  precision: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.array,
  ]),
  conversion: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]),
  formatMessage: PropTypes.func,
}

function TelemetryGeneric (props) {
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)

  const {
    device,
    periodStart,
    periodStop,
    sensorData,
    metricReady,
    intl: { formatMessage },
  } = props

  const getNewData = useCallback((address, start, stop) => {
    AppDispatcher.dispatch({
      action: 'get-metric-address',
      address: address || device.address,
      timeBeg: start || periodStart,
      timeEnd: stop || periodStop,
    })
  }, [device.address, periodStart, periodStop])

  useEffect(() => {
    getNewData()
  }, [getNewData])

  useEffect(() => {
    const interval = setInterval(getNewData, 60000)
    return () => clearInterval(interval)
  }, [getNewData])

  useEffect(() => {
    getNewData(null, periodStart, periodStop)
  }, [periodStart, periodStop, getNewData])

  if (
    !Array.isArray(sensorData) ||
    sensorData.length < 1 ||
    !metricReady
  ) {
    return (
      <ChartDataLoading />
    )
  }

  let valueKey = '178'
  if (searchParams.get('valueKey')) {
    const valueKeyParam = searchParams.get('valueKey')
    if (valueKeyParam.indexOf(',') > -1) {
      valueKey = valueKeyParam.split(',')
    } else {
      valueKey = valueKeyParam
    }
  }

  let formula = ''
  if (searchParams.get('formula')) {
    const formulaParam = searchParams.get('formula')
    if (formulaParam.indexOf(',') > -1) {
      formula = formulaParam.split(',')
    } else {
      formula = formulaParam
    }
  }

  let precision = 0
  if (searchParams.get('precision')) {
    const precisionParam = searchParams.get('precision')
    if (!isNaN(precisionParam) || precisionParam.indexOf(',') > -1) {
      if (precisionParam.indexOf(',') > -1) {
        precision = precisionParam.split(',').map(Number)
      } else {
        precision = Number(precisionParam)
      }
    }
  }

  let label = valueKey
  if (searchParams.get('label')) {
    const labelParam = searchParams.get('label')
    if (labelParam.indexOf(',') > -1) {
      label = labelParam.split(',')
    } else {
      label = labelParam
    }
  }

  let multiplier = 100
  if (searchParams.get('multiplier')) {
    const multiplierParam = searchParams.get('multiplier')
    if (!isNaN(multiplierParam) || multiplierParam.indexOf(',') > -1) {
      if (multiplierParam.indexOf(',') > -1) {
        multiplier = multiplierParam.split(',').map(Number)
      } else {
        multiplier = Number(multiplierParam)
      }
    }
  }

  let vMax = null
  if (searchParams.get('vMax')) {
    const vMaxParam = searchParams.get('vMax')
    if (!isNaN(vMaxParam)) {
      vMax = Number(vMaxParam)
    }
  }

  let vMin = null
  if (searchParams.get('vMin')) {
    const vMinParam = searchParams.get('vMin')
    if (!isNaN(vMinParam)) {
      vMin = Number(vMinParam)
    }
  }

  let stacked = true
  if (searchParams.get('stacked')) {
    const stackedParam = searchParams.get('stacked')
    if (stackedParam === 'true' || stackedParam === 'false') {
      if (stackedParam === 'true') {
        stacked = true
      } else if (stackedParam === 'false') {
        stacked = false
      }
    }
  }

  let chartType = 'AreaChart'
  if (searchParams.get('chartType')) {
    chartType = searchParams.get('chartType')
  }

  return (
    <div>
      <ChartGeneric
        sensorData={sensorData}
        metricReady={metricReady}
        device={device}
        dataType={valueKey}
        conversion={formula}
        dataName={label}
        multiplier={multiplier}
        precision={precision}
        vMax={vMax}
        vMin={vMin}
        stacked={stacked}
        chartType={chartType}
      />
      <Table
        sensorData={sensorData}
        device={device}
        dataType={valueKey}
        conversion={formula}
        dataName={label}
        multiplier={multiplier}
        precision={precision}
        formatMessage={formatMessage}
      />
    </div>
  )
}

TelemetryGeneric.propTypes = {
  device: PropTypes.shape({
    address: PropTypes.string,
  }),
  periodStart: PropTypes.string,
  periodStop: PropTypes.string,
  sensorData: PropTypes.arrayOf(PropTypes.object),
  metricReady: PropTypes.bool,
  intl: PropTypes.object,
}

export default injectIntl(TelemetryGeneric)
